import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Table,
    Card,
    Row,
    Col,
    Input,
    Button,
    Space,
    Tag,
    Typography,
    Divider,
    message,
    Form,
    Select,
    Badge,
    Tooltip,
    Tabs,
    Drawer,
    Radio,
    Descriptions,
    Upload,
    Image,
    Modal,
    Checkbox
} from 'antd';
import {
    AppleOutlined,
    AndroidOutlined,
    GlobalOutlined,
    CloudServerOutlined,
    DownloadOutlined,
    EditOutlined,
    PlusOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;

const AppManageList = () => {
    const navigate = useNavigate();
    const [downloadVisible, setDownloadVisible] = useState(false);
    const [downloadApp, setDownloadApp] = useState(null);
    const [downloadForm] = Form.useForm();
    const [editAppVisible, setEditAppVisible] = useState(false);
    const [, setEditAppRecord] = useState(null);
    const [editAppForm] = Form.useForm();
    const [searchForm] = Form.useForm();
    const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
    const [detailRecord, setDetailRecord] = useState(null);
    const [configPayVisible, setConfigPayVisible] = useState(false);
    const [configPayRecord, setConfigPayRecord] = useState(null);
    const [configPayForm] = Form.useForm();
    const [regionModalOpen, setRegionModalOpen] = useState(false);
    const [editingRegion, setEditingRegion] = useState(null);
    const [regionOverrides, setRegionOverrides] = useState([]);
    const [regionFormMode, setRegionFormMode] = useState('custom');
    const [regionFormChannels, setRegionFormChannels] = useState(['Google Pay']);
    const [regionFormCountry, setRegionFormCountry] = useState('日本（JP）');
    const allChannels = ['Google Pay', 'Apple Pay', 'PayPal', '微信支付', '支付宝'];

    // 运营方式编辑状态（key: bundleId, value: 'platform' | 'self'）
    const [editModes, setEditModes] = useState({});
    const modeRef = useRef('self');
    // 初始化 mock 数据默认值
    React.useEffect(() => {
        const initial = {};
        appFullData.forEach(app => {
            app.platforms.forEach(p => {
                initial[p.bundleId] = p.operationMode || 'self';
            });
        });
        setEditModes(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 模拟应用全量数据（分层展示：App名称 + 系统平台列表）
    const appFullData = [
        {
            key: '1',
            appName: '鹤梦智能',
            enterprise: '鹤梦科技',
            payType: 'platform',
            platforms: [
                { platform: 'iOS', region: 'mainland', bundleId: 'com.hm.smart.ios', payChannels: ['微信', '支付宝', 'Apple Pay'], status: 'pass', auditor: 'admin_audit', auditTime: '2026-03-01 10:00', createTime: '2026-02-28 09:30:00', merchant: '支付宝：鹤梦alipay', operationMode: 'platform' },
                { platform: 'Android', region: 'mainland', bundleId: 'com.hm.smart.android', payChannels: ['微信', '支付宝'], status: 'pass', auditor: 'admin_audit', auditTime: '2026-03-02 11:30', createTime: '2026-02-28 10:15:00', merchant: '微信：鹤梦wxpay', operationMode: 'self' },
                { platform: '微信小程序', region: 'mainland', bundleId: 'wx12345678', payChannels: ['微信支付'], status: 'draft', auditor: '—', auditTime: '—', createTime: '2026-04-15 17:50:09', merchant: '—', operationMode: 'self' },
            ]
        },
        {
            key: '2',
            appName: '鹤梦云联',
            enterprise: '鹤梦分行',
            payType: 'firm',
            platforms: [
                { platform: 'iOS', region: 'global', bundleId: 'com.hm.cloud.ios', payChannels: ['PayPal', 'Credit Card'], status: 'pass', auditor: 'super_admin', auditTime: '2026-02-15 09:20', createTime: '2026-02-10 14:00:00', merchant: 'PayPal：hmcloud', operationMode: 'self', payConfig: { 'Google Pay': { enabled: true, accountId: '22222' }, 'Apple Pay': { enabled: true, accountId: '11111' } } },
                { platform: 'Android', region: 'global', bundleId: 'com.hm.cloud.android', payChannels: ['Stripe', 'Google Pay'], status: 'reject', auditor: 'system_manager', auditTime: '2026-02-20 16:45', rejectReason: '包名与已有应用重复', createTime: '2026-02-10 15:30:00', merchant: 'Stripe：hmcloud_stripe', operationMode: 'self', payConfig: { '微信支付': { enabled: true, accountId: '11111' }, '支付宝': { enabled: false } } },
            ]
        }
    ];

    // 渲染嵌套的平台列表
    const expandedRowRender = (record) => {
        const columns = [
            { 
                title: '系统平台', 
                dataIndex: 'platform', 
                key: 'platform',
                render: (text) => (
                    <Space>
                        {text === 'iOS' ? <AppleOutlined /> : text === 'Android' ? <AndroidOutlined /> : <GlobalOutlined />}
                        {text}
                    </Space>
                )
            },
            { title: 'AppID', dataIndex: 'bundleId', key: 'bundleId' },
            { 
                title: '收款通道', 
                dataIndex: 'payChannels', 
                key: 'payChannels',
                width: 200,
                render: (channels) => (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {channels?.map(channel => (
                            <Tag key={channel} color="default" style={{ margin: 0 }}>{channel}</Tag>
                        ))}
                    </div>
                )
            },
            { 
                title: '运营方式', 
                key: 'operationMode',
                width: 160,
                render: (_, item) => {
                    const mode = editModes[item.bundleId] || 'self';
                    return (
                        <Space>
                            <Tag color={mode === 'platform' ? 'blue' : 'default'} style={{ border: 'none' }}>
                                {mode === 'platform' ? '平台代运营' : '企业自运营'}
                            </Tag>
                            <EditOutlined
                                style={{ color: '#1890ff', cursor: 'pointer', fontSize: 13 }}
                                onClick={() => {
                                    Modal.confirm({
                                        title: `修改运营方式 — ${item.platform}`,
                                        icon: null,
                                        width: 400,
                                        content: (
                                            <div style={{ paddingTop: 16 }}>
                                                <Radio.Group
                                                    defaultValue={mode}
                                                    onChange={(e) => { modeRef.current = e.target.value; }}
                                                >
                                                    <Space direction="vertical">
                                                        <Radio value="self">企业自运营</Radio>
                                                        <Radio value="platform">平台代运营</Radio>
                                                    </Space>
                                                </Radio.Group>
                                            </div>
                                        ),
                                        okText: '确认修改',
                                        cancelText: '取消',
                                        onOk: () => {
                                            const newMode = modeRef.current || mode;
                                            setEditModes(prev => ({ ...prev, [item.bundleId]: newMode }));
                                            message.success(`已将 ${item.platform} 运营方式切换为「${newMode === 'platform' ? '平台代运营' : '企业自运营'}」`);
                                        },
                                    });
                                }}
                            />
                        </Space>
                    );
                },
            },
            { 
                title: '上架区域', 
                dataIndex: 'region', 
                key: 'region',
                render: (text) => {
                    const map = { mainland: '国内', global: '全球', overseas: '非大陆' };
                    return map[text] || text;
                }
            },
            { 
                title: '审核信息', 
                key: 'auditInfo',
                render: (_, item) => (
                    <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                        <div>审核人：{item.auditor}</div>
                        <div>时间：{item.auditTime}</div>
                    </div>
                )
            },
            { 
                title: '状态', 
                dataIndex: 'status', 
                key: 'status',
                render: (status, item) => {
                    const config = {
                        pass: { color: 'green', text: '已生效' },
                        pending: { color: 'gold', text: '审核中' },
                        reject: { color: 'red', text: '已驳回' },
                        draft: { color: 'default', text: '待提交' },
                    };
                    const statusConfig = config[status] || { color: 'default', text: status };
                    return (
                        <Space direction="vertical" align="start" size={0}>
                            <Tag color={statusConfig.color}>{statusConfig.text}</Tag>
                            {status === 'reject' && (
                                <Tooltip title={item.rejectReason}>
                                    <div style={{ 
                                        color: '#ff4d4f', 
                                        fontSize: '11px', 
                                        marginTop: '4px',
                                        maxWidth: '120px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        cursor: 'help'
                                    }}>
                                        原因：{item.rejectReason}
                                    </div>
                                </Tooltip>
                            )}
                        </Space>
                    );
                }
            },
            {
                title: '操作',
                key: 'action',
                width: 200,
                align: 'center',
                render: (_, item) => {
                    return (
                    <Space size={0} split={<Divider type="vertical" style={{ margin: '0 2px' }} />}>
                        <Button type="link" size="small" onClick={() => {
                            setConfigPayRecord({ version: item, app: record });
                            if (record.payType === 'firm') {
                                configPayForm.setFieldsValue({
                                    payConfig: item.payConfig || {}
                                });
                            } else {
                                configPayForm.setFieldsValue({
                                    payChannels: item.payChannels || []
                                });
                            }
                            setConfigPayVisible(true);
                        }}>配置支付</Button>
                        <Button type="link" size="small" onClick={() => { setDetailRecord(item); setDetailDrawerVisible(true); }}>详情</Button>
                    </Space>
                );
            }
        },
    ];

        return <Table columns={columns} dataSource={record.platforms} pagination={false} size="small" rowKey="bundleId" />;
    };

    const handleSavePayConfig = async () => {
        try {
            await configPayForm.validateFields();
            message.success('支付配置已保存');
            setConfigPayVisible(false);
            configPayForm.resetFields();
        } catch (e) { /* validation error */ }
    };

    return (
        <div style={{ padding: '24px', backgroundColor: '#fff', minHeight: '100vh' }}>
      <div style={{ marginBottom: 16 }}>
        <Title level={4}>管理App</Title>
      </div>
            <Card bordered={false} style={{ marginBottom: 16 }}>
                <Form form={searchForm} layout="horizontal" initialValues={{ enterpriseSearchType: 'enterpriseName', appSearchType: 'appName' }}>
                    <Row gutter={[24, 16]}>
                        <Col span={8}>
                            <Input.Group compact style={{ display: 'flex' }}>
                                <Form.Item name="appSearchType" noStyle>
                                    <Select style={{ width: '120px' }}>
                                        <Option value="appName">应用名称</Option>
                                    </Select>
                                </Form.Item>
                                <Form.Item name="appKeyword" noStyle>
                                    <Input style={{ flex: 1 }} placeholder="请输入关键字" allowClear />
                                </Form.Item>
                            </Input.Group>
                        </Col>
                        <Col span={8}>
                            <Input.Group compact style={{ display: 'flex' }}>
                                <Form.Item name="enterpriseSearchType" noStyle>
                                    <Select style={{ width: '120px' }}>
                                        <Option value="enterpriseName">企业名称</Option>
                                        <Option value="enterpriseId">企业ID</Option>
                                    </Select>
                                </Form.Item>
                                <Form.Item name="enterpriseKeyword" noStyle>
                                    <Input style={{ flex: 1 }} placeholder="请输入关键字" allowClear />
                                </Form.Item>
                            </Input.Group>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="AppID" name="bundleId" style={{ marginBottom: 0 }}>
                                <Input placeholder="请输入平台 AppID" allowClear style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="系统平台" name="platform" style={{ marginBottom: 0 }}>
                                <Select placeholder="全部" allowClear style={{ width: '100%' }}>
                                    <Option value="Android">Android</Option>
                                    <Option value="iOS">iOS</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="状态" name="status" style={{ marginBottom: 0 }}>
                                <Select placeholder="全部" allowClear style={{ width: '100%' }}>
                                    <Option value="pending">审核中</Option>
                                    <Option value="reject">已驳回</Option>
                                    <Option value="pass">已生效</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="运营方式" name="operationMode" style={{ marginBottom: 0 }}>
                                <Select placeholder="全部" allowClear style={{ width: '100%' }}>
                                    <Option value="platform">平台代运营</Option>
                                    <Option value="self">企业自运营</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8} style={{ textAlign: 'right' }}>
                            <Space size="middle">
                                <Button type="primary" onClick={() => message.info('搜索中...')}>查询</Button>
                                <Button onClick={() => searchForm.resetFields()}>重置</Button>
                            </Space>
                        </Col>
                    </Row>
                </Form>
            </Card>

            <Table
                columns={[
                {
                    title: '应用基本信息',
                    dataIndex: 'appName',
                    key: 'appName',
                    render: (text, record) => (
                        <Space size={12}>
                            <CloudServerOutlined style={{ fontSize: 24, color: '#1890ff' }} />
                            <div>
                                <div style={{ fontWeight: 600 }}>{text} <EditOutlined style={{ fontSize: 12, color: '#1890ff', cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); setEditAppRecord(record); editAppForm.setFieldsValue({ appName: record.appName }); setEditAppVisible(true); }} /></div>
                            </div>
                        </Space>
                    )
                },
                {
                    title: '收款方式',
                    dataIndex: 'payType',
                    key: 'payType',
                    render: (text) => text === 'platform' ? '平台代收款' : '企业自收款'
                },
                {
                    title: '所属企业',
                    dataIndex: 'enterprise',
                    key: 'enterprise',
                    render: (text, record) => (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <Text strong>{text}</Text>
                            <Text type="secondary" style={{ fontSize: '12px' }}>ID: E-HM-{record.key}001</Text>
                        </div>
                    )
                },
                {
                    title: '覆盖平台',
                    key: 'platformStats',
                    render: (_, record) => (
                        <Space>
                            <Tag color="blue">{record.platforms.length} 个端</Tag>
                            <Tag color="cyan">已生效 {record.platforms.filter(p => p.status === 'pass').length} 个</Tag>
                        </Space>
                    )
                },
                {
                    title: '运营标签',
                    key: 'operationTags',
                    render: (_, record) => (
                        <Space size={4} wrap>
                            {record.platforms.map(p => {
                                const mode = editModes[p.bundleId] || p.operationMode || 'self';
                                return (
                                    <Tag key={p.bundleId} color={mode === 'platform' ? 'blue' : 'default'} style={{ border: 'none', margin: 0 }}>
                                        {p.platform} · {mode === 'platform' ? '平台代运营' : '企业自运营'}
                                    </Tag>
                                );
                            })}
                        </Space>
                    )
                },
                {
                    title: '操作',
                    key: 'action',
                    width: 200,
                    align: 'center',
                    render: (_, record) => (
                        <Space size={0} split={<Divider type="vertical" style={{ margin: '0 2px' }} />}>
                            <Button type="link" size="small"
                                onClick={(e) => { e.stopPropagation(); setDownloadApp(record); downloadForm.resetFields(); setDownloadVisible(true); }}
                            >下载配置</Button>
                            <Button type="link" size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/app/manage/operation-config/${record.key}`, { state: { appName: record.appName } });
                                }}
                            >运营配置</Button>
                        </Space>
                    )
                }
            ]}
                dataSource={appFullData}
                expandable={{
                    expandedRowRender,
                    defaultExpandAllRows: true, // 默认展开，方便管理员全局查看
                }}
                rowKey="key"
                pagination={{
                    showTotal: (total) => `共 ${total} 个应用`,
                }}
                style={{ border: '1px solid #f0f0f0', borderRadius: '8px' }}
            />

            {/* 下载管理 */}
            <Drawer
                title={<Space><DownloadOutlined /><span>下载配置 - {downloadApp?.appName}</span></Space>}
                width={600}
                onClose={() => setDownloadVisible(false)}
                open={downloadVisible}
                destroyOnClose
                footer={
                    <div style={{ textAlign: 'right', padding: '10px 16px' }}>
                        <Button onClick={() => setDownloadVisible(false)} style={{ marginRight: 8 }}>取消</Button>
                        <Button type="primary" onClick={() => { message.success('下载页配置已保存'); setDownloadVisible(false); }}>保存</Button>
                    </div>
                }
            >
                <Form form={downloadForm} layout="vertical">
                    <div style={{ background: '#fff7e6', padding: '10px 14px', borderRadius: 6, marginBottom: 16, border: '1px solid #ffe58f' }}>
                        <Text style={{ fontSize: 13 }}>用户在下载App应用，系统根据手机品牌自动跳转对应渠道。对于Android操作系统，未匹配到下载渠道，平台会根据你配置的安装包引导用户安装应用。</Text>
                    </div>
                    <Text strong style={{ display: 'block', marginBottom: 12 }}>下载地址</Text>
                    <Form.Item name={['default', 'domesticAndroid']} label="国内Android（应用宝）" style={{ marginBottom: 8 }}><Input placeholder="https://a.app.qq.com/o/simple.jsp?pkgname=xxx" /></Form.Item>
                    <Form.Item name={['default', 'overseasAndroid']} label="海外Android（Google商店）" style={{ marginBottom: 8 }}><Input placeholder="https://play.google.com/store/apps/details?id=xxx" /></Form.Item>
                    <Form.Item name={['default', 'apple']} label="Apple" style={{ marginBottom: 8 }}><Input placeholder="https://apps.apple.com/app/idxxxxxxx" /></Form.Item>
                    <Tabs defaultActiveKey="domestic" style={{ marginTop: 8 }} items={[
                        { key: 'domestic', label: '国内Android', children: (
                            <>{['小米', 'vivo', 'OPPO', '三星'].map((brand) => (
                                <Form.Item key={brand} name={['domestic', brand]} label={brand} labelCol={{ style: { width: 70, textAlign: 'right' } }} style={{ marginBottom: 8 }}><Input placeholder={`${brand}应用商店下载链接（选填）`} /></Form.Item>
                            ))}</>
                        )},
                        { key: 'overseas', label: '海外Android', children: (
                            <div style={{ padding: '24px 0', textAlign: 'center' }}><Text type="secondary">暂无海外Android渠道配置</Text></div>
                        )},
                    ]} />
                    <Divider style={{ margin: '16px 0' }} />
                    <Text strong style={{ display: 'block', marginBottom: 12 }}>Android安装包</Text>
                    <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
                        <Form.Item name="apkDomestic" label="国内Android" style={{ flex: 1, marginBottom: 0 }}><Input placeholder="请输入国内Android安装包地址" /></Form.Item>
                        <Form.Item name="apkOverseas" label="海外Android" style={{ flex: 1, marginBottom: 0 }}><Input placeholder="请输入海外Android安装包地址" /></Form.Item>
                    </div>
                    <Divider style={{ margin: '16px 0' }} />
                    <Text strong style={{ display: 'block', marginBottom: 12 }}>下载详情页图片</Text>
                    <Form.Item name="detailImage" label="详情图片" extra="用于下载页底部展示，建议宽度750px">
                        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end' }}>
                            <Upload listType="picture-card" maxCount={1} beforeUpload={() => false}>
                                <div><PlusOutlined /><div style={{ marginTop: 8, fontSize: 12 }}>上传图片</div></div>
                            </Upload>
                            <div style={{ marginBottom: 8 }}>
                                <Image width={1} height={1} src="/demo1.png" preview={{ src: '/demo1.png' }} id="auditPreviewImg" style={{ opacity: 0, position: 'absolute' }} />
                                <Button type="primary" ghost size="small" onClick={() => document.getElementById('auditPreviewImg')?.click()}>预览下载页</Button>
                            </div>
                        </div>
                    </Form.Item>
                </Form>
            </Drawer>

            {/* 修改应用信息 */}
            <Modal title="修改应用信息" open={editAppVisible}
                onOk={async () => { try { await editAppForm.validateFields(); message.success('应用信息已更新'); setEditAppVisible(false); } catch(e){} }}
                onCancel={() => setEditAppVisible(false)} okText="保存" cancelText="取消" destroyOnClose width={520}
            >
                <Form form={editAppForm} layout="vertical" style={{ marginTop: 16 }}>
                    <Form.Item name="appName" label="应用名称" rules={[{ required: true, message: '请输入应用名称' }]}>
                        <Input placeholder="请输入应用名称（默认中文）" maxLength={40} showCount />
                    </Form.Item>
                    <Form.Item label="应用LOGO" name="appLogo" valuePropName="fileList" getValueFromEvent={(e) => Array.isArray(e) ? e : e?.fileList} extra="建议尺寸 200*200，支持 png、jpg 格式">
                        <Upload listType="picture-card" maxCount={1} beforeUpload={() => false}>
                            <div><PlusOutlined /><div style={{ marginTop: 8 }}>上传图片</div></div>
                        </Upload>
                    </Form.Item>
                </Form>
            </Modal>

            {/* 版本详情 */}
            <Drawer title="版本详情" width={560} open={detailDrawerVisible} onClose={() => setDetailDrawerVisible(false)}>
                {detailRecord && (
                    <>
                        <Title level={5}>基本信息</Title>
                        <Descriptions column={1} bordered size="small" labelStyle={{ width: 120, background: '#fafafa' }}>
                            <Descriptions.Item label="当前状态">
                                <Badge status={{ pass: 'success', pending: 'warning', reject: 'error', draft: 'default' }[detailRecord.status] || 'default'}
                                    text={{ pass: '已生效', pending: '审核中', reject: '已驳回', draft: '待提交' }[detailRecord.status] || detailRecord.status} />
                            </Descriptions.Item>
                            <Descriptions.Item label="系统平台">{detailRecord.platform}</Descriptions.Item>
                            <Descriptions.Item label="上架区域">{{ mainland: '国内', global: '全球', overseas: '非大陆' }[detailRecord.region] || detailRecord.region}</Descriptions.Item>
                            <Descriptions.Item label="AppID"><Text copyable>{detailRecord.bundleId}</Text></Descriptions.Item>
                            <Descriptions.Item label="收款商户号">{detailRecord.merchant || '—'}</Descriptions.Item>
                        </Descriptions>
                        <Divider />
                        <Title level={5}>审核信息</Title>
                        <Descriptions column={1} bordered size="small" labelStyle={{ width: 120, background: '#fafafa' }}>
                            <Descriptions.Item label="创建时间">{detailRecord.createTime || '—'}</Descriptions.Item>
                            <Descriptions.Item label="审核时间">{detailRecord.auditTime || '—'}</Descriptions.Item>
                            <Descriptions.Item label="审核人员">{detailRecord.auditor || '—'}</Descriptions.Item>
                        </Descriptions>
                    </>
                )}
            </Drawer>

            {/* 配置支付抽屉 */}
            <Drawer
                title={`配置支付 - ${configPayRecord?.version?.platform || ''}平台`}
                width={560}
                onClose={() => { setConfigPayVisible(false); configPayForm.resetFields(); }}
                open={configPayVisible}
                destroyOnClose
                footer={
                    <div style={{ textAlign: 'right', padding: '10px 16px' }}>
                        <Button onClick={() => { setConfigPayVisible(false); configPayForm.resetFields(); }} style={{ marginRight: 8 }}>取消</Button>
                        <Button type="primary" onClick={handleSavePayConfig}>保存</Button>
                    </div>
                }
            >
                {configPayRecord && (
                    <>
                        <div style={{ background: '#f9f9f9', padding: '16px', borderRadius: '8px', marginBottom: '24px' }}>
                            <Descriptions column={1} size="small" colon={false} labelStyle={{ width: '80px', color: '#8c8c8c' }}>
                                <Descriptions.Item label="系统平台">
                                    <Space>
                                        {configPayRecord.version.platform === 'iOS' ? <AppleOutlined /> : 
                                         configPayRecord.version.platform === 'Android' ? <AndroidOutlined /> : <GlobalOutlined />}
                                        {configPayRecord.version.platform}
                                    </Space>
                                </Descriptions.Item>
                                <Descriptions.Item label="AppID">
                                    <Text copyable>{configPayRecord.version.bundleId}</Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="收款方式">
                                    <Tag color={configPayRecord.app.payType === 'platform' ? 'blue' : 'orange'} style={{ border: 'none' }}>
                                        {configPayRecord.app.payType === 'platform' ? '平台代收款' : '企业自收款'}
                                    </Tag>
                                </Descriptions.Item>
                            </Descriptions>
                        </div>

                        <Form form={configPayForm} layout="vertical">
                            <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 12 }}>默认支付方式</Text>
                            {configPayRecord.app.payType === 'firm' ? (
                                <Form.Item label="收款商户号" required tooltip="请选择支付通道并指定已配置的商户号">
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        {[
                                            { id: 'Google Pay', label: 'Google Pay', color: '#4285F4' },
                                            { id: 'Apple Pay', label: 'Apple Pay', color: '#000000' },
                                            { id: 'PayPal', label: 'PayPal', color: '#003087' },
                                            { id: '微信支付', label: '微信支付', color: '#1AAD19' },
                                            { id: '支付宝', label: '支付宝', color: '#1677FF' },
                                        ].map(channel => (
                                            <div key={channel.id} style={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: '12px',
                                                padding: '12px',
                                                border: '1px solid #f0f0f0',
                                                borderRadius: '8px',
                                                marginBottom: '8px'
                                            }}>
                                                <Form.Item name={['payConfig', channel.id, 'enabled']} valuePropName="checked" noStyle>
                                                    <Checkbox>
                                                        <Tag color={channel.color} style={{ margin: 0, minWidth: '80px', textAlign: 'center' }}>
                                                            {channel.label}
                                                        </Tag>
                                                    </Checkbox>
                                                </Form.Item>
                                                
                                                <Form.Item 
                                                    noStyle
                                                    shouldUpdate={(prevValues, currentValues) => 
                                                        prevValues.payConfig?.[channel.id]?.enabled !== currentValues.payConfig?.[channel.id]?.enabled
                                                    }
                                                >
                                                    {({ getFieldValue }) => {
                                                        const isEnabled = getFieldValue(['payConfig', channel.id, 'enabled']);
                                                        return (
                                                            <Form.Item 
                                                                name={['payConfig', channel.id, 'accountId']} 
                                                                noStyle
                                                                rules={[{ required: isEnabled, message: '请选择商户号' }]}
                                                            >
                                                                <Select 
                                                                    placeholder="请选择已配置的商户号" 
                                                                    disabled={!isEnabled}
                                                                    style={{ flex: 1 }}
                                                                    allowClear
                                                                >
                                                                    <Option value="11111">测试账号 (11111)</Option>
                                                                    <Option value="22222">正式账号 (22222)</Option>
                                                                </Select>
                                                            </Form.Item>
                                                        );
                                                    }}
                                                </Form.Item>
                                                <Button type="link" size="small" style={{ fontSize: '12px', padding: 0 }}>
                                                    还没账号? 前往添加&gt;&gt;
                                                </Button>
                                            </div>
                                        ))}
                                    </Space>
                                </Form.Item>
                            ) : (
                                <Form.Item label="选择支付通道" name="payChannels" rules={[{ required: true, message: '请至少选择一个支付通道' }]}>
                                    <Checkbox.Group style={{ width: '100%' }}>
                                        <Row gutter={[16, 16]}>
                                            {[
                                                { label: 'Google Pay', value: 'Google Pay' },
                                                { label: 'Apple Pay', value: 'Apple Pay' },
                                                { label: 'PayPal', value: 'PayPal' },
                                                { label: '微信支付', value: '微信支付' },
                                                { label: '支付宝', value: '支付宝' },
                                            ].map(item => (
                                                <Col span={8} key={item.value}>
                                                    <Checkbox value={item.value}>{item.label}</Checkbox>
                                                </Col>
                                            ))}
                                        </Row>
                                    </Checkbox.Group>
                                </Form.Item>
                            )}
                        </Form>

                        <Divider style={{ margin: '20px 0' }} />
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                            <Text strong style={{ fontSize: 14 }}>区域支付覆盖（可选）</Text>
                            <Button type="link" size="small" onClick={() => { setEditingRegion(null); setRegionFormMode('custom'); setRegionFormChannels(['Google Pay']); setRegionFormCountry('日本（JP）'); setRegionModalOpen(true); }}>添加区域配置</Button>
                        </div>
                        {regionOverrides.length > 0 ? (
                            <Table size="small" pagination={false} dataSource={regionOverrides} rowKey="country"
                                columns={[
                                    { title: '国家/区域', dataIndex: 'country', width: 120, render: v => <Text strong>{v}</Text> },
                                    { title: '支付方式', dataIndex: 'channels', render: v => v.length === 0 ? <Text type="danger">无支付（用户不可购买）</Text> : <Text>{v.join(', ')}</Text> },
                                    { title: '操作', width: 120, render: (_, r) => (
                                        <Space>
                                            <Button type="link" size="small" onClick={() => { setEditingRegion(r); setRegionFormMode(r.channels.length === 0 ? 'none' : 'custom'); setRegionFormChannels(r.channels); setRegionModalOpen(true); }}>编辑</Button>
                                            <Button type="link" size="small" danger onClick={() => { setRegionOverrides(prev => prev.filter(x => x.country !== r.country)); }}>删除</Button>
                                        </Space>
                                    )},
                                ]}
                            />
                        ) : <Text type="secondary" style={{ fontSize: 12 }}>暂无区域配置，所有区域使用默认支付方式</Text>}
                    </>
                )}
            </Drawer>

            {/* 区域支付配置 Modal */}
            <Modal title={editingRegion ? '编辑区域支付配置' : '添加区域支付配置'} open={regionModalOpen}
                onCancel={() => setRegionModalOpen(false)} 
                onOk={() => {
                    const channels = regionFormMode === 'none' ? [] : regionFormChannels;
                    if (editingRegion) { setRegionOverrides(prev => prev.map(r => r.country === editingRegion.country ? { ...r, channels } : r)); }
                    else { if (regionOverrides.find(r => r.country === regionFormCountry)) { message.warning('该国家已有区域配置'); return; } setRegionOverrides(prev => [...prev, { country: regionFormCountry, channels }]); }
                    setRegionModalOpen(false);
                }} okText="保存" cancelText="取消" destroyOnClose width={420}>
                <div style={{ marginTop: 16 }}>
                    <Text strong>国家/区域：</Text>
                    <Select value={regionFormCountry} onChange={setRegionFormCountry} placeholder="选择国家" style={{ width: '100%', marginTop: 8, marginBottom: 16 }}>
                        {['日本（JP）', '巴西（BR）', '印度（IN）', '韩国（KR）'].filter(c => !regionOverrides.find(r => r.country === c) || editingRegion?.country === c).map(c => <Option key={c} value={c}>{c}</Option>)}
                    </Select>
                    <Text strong>支付方式：</Text>
                    <Radio.Group value={regionFormMode} onChange={e => { setRegionFormMode(e.target.value); if (e.target.value === 'none') setRegionFormChannels([]); }} style={{ marginTop: 8, display: 'block' }}>
                        <Space direction="vertical"><Radio value="custom">自定义支付方式</Radio><Radio value="none">不配置支付（该国家用户不可购买）</Radio></Space>
                    </Radio.Group>
                    {regionFormMode === 'custom' && (
                        <div style={{ marginTop: 12 }}>
                            {configPayRecord?.app?.payType === 'firm' ? (
                                allChannels.map(c => (
                                    <div key={c} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, border: '1px solid #f0f0f0', borderRadius: 8, marginBottom: 8 }}>
                                        <Checkbox checked={regionFormChannels.includes(c)} onChange={e => { setRegionFormChannels(e.target.checked ? [...regionFormChannels, c] : regionFormChannels.filter(x => x !== c)); }}>
                                            <Tag style={{ margin: 0, minWidth: 80, textAlign: 'center' }}>{c}</Tag>
                                        </Checkbox>
                                        <Select placeholder="选择商户号" disabled={!regionFormChannels.includes(c)} style={{ flex: 1 }}><Option value="11111">测试账号 (11111)</Option><Option value="22222">正式账号 (22222)</Option></Select>
                                    </div>
                                ))
                            ) : (
                                <Checkbox.Group value={regionFormChannels} onChange={setRegionFormChannels} style={{ width: '100%' }}>
                                    <Row gutter={[8, 8]}>{allChannels.map(c => <Col span={12} key={c}><Checkbox value={c}>{c}</Checkbox></Col>)}</Row>
                                </Checkbox.Group>
                            )}
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default AppManageList;
